// Menu Hamburger — Style Google / Airbnb Premium
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, Settings, User, Heart,
  Calendar, Mail, MessageCircle, BarChart3, FileText, LogOut, HelpCircle, Mic, ArrowUp,
} from 'lucide-react';

const ALL_UNIVERSES = [
  { id: 'abracadabati',    suffix: 'Bati',    icon: '🔨', active: true,  path: '/' },
  { id: 'abracadahome',    suffix: 'Home',    icon: '🏠' },
  { id: 'abracadamatch',   suffix: 'Match',   icon: '❤️' },
  { id: 'abracadaevent',   suffix: 'Event',   icon: '🎉' },
  { id: 'abracadazoo',     suffix: 'Zoo',     icon: '🐾' },
  { id: 'abracadatravel',  suffix: 'Travel',  icon: '✈️' },
  { id: 'abracadafashion', suffix: 'Fashion', icon: '👗' },
  { id: 'abracadatech',    suffix: 'Tech',    icon: '💻' },
  { id: 'abracadarealty',  suffix: 'Realty',  icon: '🏡' },
  { id: 'abracadabeauty',  suffix: 'Beauty',  icon: '✨' },
  { id: 'abracadasecret',  suffix: 'Secret',  icon: '🔐' },
  { id: 'abracadasport',   suffix: 'Sport',   icon: '⚽' },
  { id: 'abracadagreen',   suffix: 'Green',   icon: '🌿' },
  { id: 'abracadafood',    suffix: 'Food',    icon: '🍽️' },
  { id: 'abracadacar',     suffix: 'Car',     icon: '🚗' },
  { id: 'abracadaboat',    suffix: 'Boat',    icon: '⛵' },
  { id: 'abracadahealth',  suffix: 'Health',  icon: '❤️‍🩹' },
  { id: 'abracadatoys',    suffix: 'Toys',    icon: '🧸' },
  { id: 'abracadalear',    suffix: 'Learn',   icon: '📚' },
  { id: 'abracadaart',     suffix: 'Art',     icon: '🎨' },
];

const UNIVERSE_DATA = {
  abracadabati: {
    title: 'Univers du bâtiment',
    sections: [
      {
        label: 'Centre commercial', path: '/produits',
        chips: [
          { label: 'Artisans',         path: '/artisans' },
          { label: 'Vendeurs pros',    path: '/vendeurs-pros' },
          { label: 'Petites annonces', path: '/entre-particuliers' },
          { label: 'Neuf',             path: '/neuf' },
          { label: 'Services',         path: '/services' },
        ],
      },
      {
        label: 'Réseaux sociaux', path: '/reseau',
        chips: [
          { label: 'Actus',      path: '/actus' },
          { label: 'Vidéos',     path: '/videos' },
          { label: 'Photos',     path: '/photos' },
          { label: 'Blogs',      path: '/blogs' },
          { label: 'Événements', path: '/evenements' },
        ],
      },
    ],
  },
};

const MON_ESPACE = [
  { label: 'Marketplace',  icon: '🛍️', bg: 'rgba(249,115,22,0.1)',  path: '/produits' },
  { label: 'Prestataires', icon: '🔧', bg: 'rgba(59,130,246,0.1)',  path: '/artisans' },
  { label: 'Emplois',      icon: '💼', bg: 'rgba(34,197,94,0.1)',   path: '/emploi' },
];

const CHEZ_MOI_TOOLS = [
  { label: 'Cockpit',  Icon: BarChart3,     color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)',  path: '/cockpit-particulier' },
  { label: 'Messages', Icon: MessageCircle, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)',  path: '/messages' },
  { label: 'Mail',     Icon: Mail,          color: '#6366F1', bg: 'rgba(99,102,241,0.1)',  path: '/emails' },
  { label: 'Agenda',   Icon: Calendar,      color: '#EF4444', bg: 'rgba(239,68,68,0.1)',   path: '/agenda' },
  { label: 'Favoris',  Icon: Heart,         color: '#EC4899', bg: 'rgba(236,72,153,0.1)',  path: '/cockpit-particulier/favoris' },
  { label: 'Demandes', Icon: FileText,      color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',  path: '/cockpit-particulier/artisans/devis' },
];

const COMPTE_ITEMS = [
  { label: 'Profil',     Icon: User,       path: '/profil' },
  { label: 'Paramètres', Icon: Settings,   path: '/parametres' },
  { label: 'Aide',       Icon: HelpCircle, path: '/aide' },
];

export default function UniversHamburgerMenu() {
  const [isOpen, setIsOpen]                           = useState(false);
  const [comingSoonId, setComingSoonId]               = useState(null);
  const [showUniversGrid, setShowUniversGrid]         = useState(false);
  const [selectedUniverseId, setSelectedUniverseId]   = useState('abracadabati');
  const [activeTabIndex, setActiveTabIndex]           = useState(0);
  const navigate  = useNavigate();
  const location  = useLocation();

  const isEspaceProRoute = location.pathname.startsWith('/pro');

  useEffect(() => {
    const root = document.getElementById('root');
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (root) {
        root.style.willChange      = 'transform';
        root.style.transition      = 'transform 0.3s ease, border-radius 0.3s ease';
        root.style.transform       = 'scale(0.93) translateY(-6px)';
        root.style.borderRadius    = '20px';
        root.style.overflow        = 'hidden';
      }
    } else {
      document.body.style.overflow = '';
      if (root) {
        root.style.transform    = '';
        root.style.borderRadius = '';
        root.style.overflow     = '';
        root.style.willChange   = '';
      }
    }
    return () => {
      document.body.style.overflow = '';
      if (root) {
        root.style.transform    = '';
        root.style.borderRadius = '';
        root.style.overflow     = '';
        root.style.willChange   = '';
      }
    };
  }, [isOpen]);

  const go    = (path) => { setIsOpen(false); setShowUniversGrid(false); setTimeout(() => navigate(path), 150); };
  const close = ()     => { setIsOpen(false); setShowUniversGrid(false); };

  const handleUniverseTap = (u) => {
    setSelectedUniverseId(u.id);
    setActiveTabIndex(0);
    if (!u.active) { setComingSoonId(u.id); setTimeout(() => setComingSoonId(null), 1800); }
  };

  if (isEspaceProRoute) return null;

  return (
    <>
      {/* ── Bouton 9 carrés ── */}
      <button
        onClick={() => setIsOpen(true)}
        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
        data-testid="hamburger-menu-btn"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-[#1E3A5F]">
          <rect x="3"  y="3"  width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="10" y="3"  width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="17" y="3"  width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="3"  y="10" width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="10" y="10" width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="17" y="10" width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="3"  y="17" width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="10" y="17" width="5" height="5" rx="1" fill="currentColor"/>
          <rect x="17" y="17" width="5" height="5" rx="1" fill="currentColor"/>
        </svg>
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                onClick={close}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99990 }}
              />

              <motion.div
                key="menu"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 34, stiffness: 320, mass: 0.85 }}
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.22 }}
                onDragEnd={(_, info) => { if (info.offset.y > 80) close(); }}
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: '#FFFBF7', zIndex: 99991,
                  display: 'flex', flexDirection: 'column',
                  overflowY: 'auto', WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                }}
              >
                {/* Drag pill */}
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 6, flexShrink: 0 }}>
                  <div style={{ width: 36, height: 4, background: '#C8CCD6', borderRadius: 2 }} />
                </div>

                {/* ── LOGO + X ── */}
                <div style={{ position: 'relative', padding: '14px 56px 20px 16px', flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 46, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
                    userSelect: 'none', display: 'inline-block', paddingBottom: 3,
                    background: 'linear-gradient(90deg,#1D3771 0%,#1672C4 20%,#0AABEC 42%,#00C2F2 55%,#FFC020 63%,#F27218 76%,#EC580C 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}>Abracada</span>
                  <button onClick={close} style={{
                    position: 'absolute', top: '50%', right: 14, transform: 'translateY(-50%)',
                    width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.07)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: 'none', cursor: 'pointer',
                  }}>
                    <X size={17} color="#1a1a1a" strokeWidth={2.5} />
                  </button>
                </div>

                {/* ── BARRE ABRA ── */}
                <div style={{ margin: '0 12px 14px', flexShrink: 0 }}>
                  <button onClick={() => go('/assistant')} style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    background: '#FFFFFF', borderRadius: 28, padding: '6px 14px',
                    border: 'none', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                  }}>
                    {/* Logo Abra — SVG inline, aucun chargement réseau, pas de fond */}
                    <div style={{
                      width: 44, height: 44, flexShrink: 0, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #F97316 0%, #E8650A 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(232,101,10,0.3)',
                    }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="0.5" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span style={{ flex: 1, textAlign: 'left', fontSize: 15, color: '#BDBDBD' }}>Demande à Abra</span>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(232,101,10,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Mic size={15} color="#E8650A" />
                    </div>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#E8650A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(232,101,10,0.35)' }}>
                      <ArrowUp size={15} color="white" strokeWidth={2.5} />
                    </div>
                  </button>
                </div>

                {/* ── UNIVERS ── */}
                <div style={{ ...CARD_BASE, marginBottom: 10 }}>
                  <div style={{ padding: '9px 14px 5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={LBL}>Univers</span>
                    <button onClick={() => setShowUniversGrid(true)} style={{ fontSize: 12, fontWeight: 600, color: '#E8650A', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Tous</button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <div style={{ overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingLeft: 14, paddingRight: 6, paddingBottom: 10 }}>
                      <div style={{ display: 'flex', gap: 10, width: 'max-content' }}>
                        {ALL_UNIVERSES.map((u) => {
                          const isBati     = u.active;
                          const isSelected = u.id === selectedUniverseId;
                          return (
                            <button key={u.id} onClick={() => handleUniverseTap(u)}
                              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, border: 'none', background: 'none', cursor: 'pointer', minWidth: 48, padding: 0, position: 'relative', flexShrink: 0 }}>
                              <div style={{
                                width: 48, height: 48, borderRadius: 14, fontSize: 20,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                position: 'relative', overflow: 'hidden',
                                background: isSelected && isBati ? 'linear-gradient(135deg,#FFF1E6,#FFD8B8)' : isSelected ? 'linear-gradient(135deg,#EEF2FF,#E0E7FF)' : '#F3F4F6',
                                border: isSelected && isBati ? '1px solid rgba(249,115,22,0.18)' : isSelected ? '1px solid rgba(99,102,241,0.18)' : '1px solid transparent',
                                boxShadow: isSelected && isBati ? '0 4px 14px rgba(249,115,22,0.18)' : isSelected ? '0 2px 8px rgba(99,102,241,0.1)' : 'none',
                              }}>
                                {u.icon}
                                {comingSoonId === u.id && (
                                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.68)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 13, fontSize: 8, fontWeight: 700, color: 'white' }}>Bientôt</div>
                                )}
                              </div>
                              {isBati && (
                                <span style={{ position: 'absolute', top: -2, right: -2, background: '#34D399', color: 'white', fontSize: 6.5, fontWeight: 700, padding: '1px 3px', borderRadius: 3, lineHeight: 1.4, whiteSpace: 'nowrap' }}>Actif</span>
                              )}
                              <span style={{ fontSize: 9.5, fontWeight: isSelected ? 700 : 500, color: isSelected && isBati ? '#F97316' : isSelected ? '#6366F1' : '#9CA3AF', lineHeight: 1.2 }}>{u.suffix}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 48, background: 'linear-gradient(to right,transparent,rgba(255,255,255,0.92))', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8, paddingBottom: 10, pointerEvents: 'none' }}>
                      <ChevronRight size={12} color="#F0A070" strokeWidth={2} />
                    </div>
                  </div>
                </div>

                {/* ── UNIVERS SÉLECTIONNÉ ── */}
                <AnimatePresence mode="wait">
                  {(() => {
                    const sel  = ALL_UNIVERSES.find(u => u.id === selectedUniverseId);
                    const data = UNIVERSE_DATA[selectedUniverseId];
                    if (!sel) return null;

                    // Teinte douce du header — très légère, ne domine pas
                    const isBati  = sel.active;
                    const cardBg  = isBati
                      ? 'linear-gradient(135deg, #FFF6F0, #FFEADE)'
                      : 'linear-gradient(135deg, #F5F7FF, #ECEFFE)';

                    if (!sel.active || !data) {
                      return (
                        <motion.div key={`sel-${selectedUniverseId}`}
                          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                          style={{ ...CARD_BASE, marginBottom: 10, padding: '11px 14px' }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#202124', marginBottom: 3 }}>Abracada{sel.suffix}</div>
                          <div style={{ fontSize: 11, color: '#B0B8C4', lineHeight: 1.4 }}>Bientôt disponible — Cet univers sera prochainement ouvert dans Abracada.</div>
                        </motion.div>
                      );
                    }

                    const activeSection = data.sections[activeTabIndex] || data.sections[0];
                    return (
                      <motion.div key={`sel-${selectedUniverseId}`}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                        style={{ ...CARD_BASE, marginBottom: 10, paddingBottom: 10 }}>
                        {/* Titre coloré — arrondi haut pour coller aux coins de la carte */}
                        <div style={{ background: cardBg, padding: '12px 14px 8px', borderRadius: '16px 16px 0 0' }}>
                          <span style={LBL}>{data.title}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 20, padding: '0 14px', borderBottom: '1.5px solid #EBEBEB', marginBottom: 9 }}>
                          {data.sections.map((section, si) => (
                            <button key={si} onClick={() => setActiveTabIndex(si)} style={{
                              fontSize: 13.5, fontWeight: si === activeTabIndex ? 700 : 500,
                              color: si === activeTabIndex ? '#202124' : '#9CA3AF',
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: '8px 0 8px',
                              borderBottom: si === activeTabIndex ? '2px solid #E8650A' : '2px solid transparent',
                              marginBottom: -2, letterSpacing: '-0.1px',
                            }}>{section.label}</button>
                          ))}
                        </div>
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.div key={activeTabIndex}
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            transition={{ duration: 0.1 }}
                            style={{ position: 'relative' }}>
                            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingLeft: 14, paddingRight: 28 }}>
                              <div style={{ display: 'flex', alignItems: 'center', width: 'max-content' }}>
                                {activeSection.chips.reduce((acc, chip, ci) => {
                                  if (ci > 0) acc.push(<span key={`dot-${ci}`} style={{ color: '#D1D5DB', padding: '0 4px', fontSize: 10, userSelect: 'none' }}>·</span>);
                                  acc.push(<button key={chip.path} onClick={() => go(chip.path)} style={{ fontSize: 12, fontWeight: 400, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0, whiteSpace: 'nowrap', lineHeight: 1.5 }}>{chip.label}</button>);
                                  return acc;
                                }, [])}
                              </div>
                            </div>
                            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 22, background: 'linear-gradient(to right,transparent,rgba(255,255,255,0.96))', pointerEvents: 'none' }} />
                          </motion.div>
                        </AnimatePresence>
                      </motion.div>
                    );
                  })()}
                </AnimatePresence>

                {/* ── MON ESPACE ── */}
                <div style={{ ...CARD_CLIP, marginBottom: 10 }}>
                  <div style={{ padding: '9px 14px 5px' }}><span style={LBL}>Mon espace</span></div>
                  {MON_ESPACE.map((item, i) => (
                    <button key={item.path} onClick={() => go(item.path)}
                      style={{ ...ROW, padding: '10px 16px', borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 9, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>{item.icon}</div>
                      <span style={{ flex: 1, textAlign: 'left', fontSize: 13.5, fontWeight: 500, color: '#202124' }}>{item.label}</span>
                      <ChevronRight size={14} color="#D1D5DB" />
                    </button>
                  ))}
                </div>

                {/* ── CHEZ MOI ── */}
                <div style={{ ...CARD_BASE, marginBottom: 10 }}>
                  <div style={{ padding: '9px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={LBL}>Chez moi</span>
                    <button onClick={() => go('/cockpit-particulier')} style={{ fontSize: 11, fontWeight: 600, color: '#E8650A', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Tout voir</button>
                  </div>
                  {/* Rangée icônes — style Apple Pay / Revolut */}
                  <div style={{ display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none', padding: '2px 12px 14px', gap: 4 }}>
                    {CHEZ_MOI_TOOLS.map(({ label, Icon, color, bg, path }) => (
                      <button key={path} onClick={() => go(path)}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, minWidth: 58, padding: '0 5px', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 13, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon size={20} color={color} />
                        </div>
                        <span style={{ fontSize: 9.5, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', letterSpacing: '-0.2px' }}>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── MON PROFIL ── */}
                <div style={{ ...CARD_CLIP, marginBottom: 16 }}>
                  {COMPTE_ITEMS.map(({ label, Icon, path }, i) => (
                    <button key={path} onClick={() => go(path)}
                      style={{ ...ROW, padding: '10px 16px', borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={14} color="#6B7280" />
                      </div>
                      <span style={{ flex: 1, textAlign: 'left', fontSize: 13.5, fontWeight: 500, color: '#202124' }}>{label}</span>
                      <ChevronRight size={13} color="#D1D5DB" />
                    </button>
                  ))}
                  <button onClick={() => {}} style={{ ...ROW, padding: '10px 16px', borderTop: '1px solid #F3F4F6' }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <LogOut size={14} color="#EF4444" />
                    </div>
                    <span style={{ flex: 1, textAlign: 'left', fontSize: 13.5, fontWeight: 600, color: '#EF4444' }}>Se déconnecter</span>
                  </button>
                </div>

                {/* Safe area iOS */}
                <div style={{ height: 'max(16px, env(safe-area-inset-bottom))', flexShrink: 0 }} />

                {/* ── GRILLE TOUS LES UNIVERS ── */}
                <AnimatePresence>
                  {showUniversGrid && (
                    <motion.div key="univers-grid"
                      initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                      transition={{ type: 'spring', damping: 34, stiffness: 320, mass: 0.85 }}
                      style={{ position: 'fixed', inset: 0, zIndex: 99993, background: '#FFF8F4', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ background: '#1a2a4a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', paddingTop: 'max(env(safe-area-inset-top),14px)', flexShrink: 0 }}>
                        <h2 style={{ color: 'white', fontWeight: 700, fontSize: 18, margin: 0 }}>Choisis ton univers</h2>
                        <button onClick={() => setShowUniversGrid(false)} style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
                          <X size={19} color="white" />
                        </button>
                      </div>
                      <div style={{ flex: 1, padding: '10px 8px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px', overflowY: 'auto' }}>
                        {ALL_UNIVERSES.map((u) => (
                          <button key={u.id} onClick={() => { handleUniverseTap(u); setShowUniversGrid(false); }}
                            style={{ borderRadius: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, position: 'relative', cursor: 'pointer', padding: '12px 4px', border: 'none', background: u.active ? 'rgba(232,101,10,0.12)' : '#f0f0f0', outline: u.active ? '1.5px solid #E8650A' : 'none' }}>
                            {u.active && <span style={{ position: 'absolute', top: -5, left: '50%', transform: 'translateX(-50%)', background: '#16a34a', color: '#fff', fontSize: 7, fontWeight: 700, padding: '1px 6px', borderRadius: 5, whiteSpace: 'nowrap' }}>Actif</span>}
                            {comingSoonId === u.id && <div style={{ position: 'absolute', inset: 0, borderRadius: 14, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white' }}>Bientôt</div>}
                            <span style={{ fontSize: 28 }}>{u.icon}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: u.active ? '#E8650A' : '#9ca3af', textAlign: 'center' }}>{u.suffix}</span>
                            {!u.active && <span style={{ fontSize: 8, color: '#9CA3AF' }}>Bientôt</span>}
                          </button>
                        ))}
                      </div>
                      <div style={{ background: '#1a2a4a', padding: '8px', paddingBottom: 'max(env(safe-area-inset-bottom),8px)', textAlign: 'center' }}>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 9, margin: 0 }}>AbracadaWorld © 2026 · {ALL_UNIVERSES.length} univers · 1 plateforme</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const CARD_BASE = {
  background: '#FFFFFF', borderRadius: 16, margin: '0 12px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
};

const CARD_CLIP = {
  background: '#FFFFFF', borderRadius: 16, margin: '0 12px',
  overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
};

const LBL = {
  fontSize: 14, fontWeight: 700, color: '#202124', letterSpacing: '-0.1px',
};

const ROW = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
  padding: '13px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
};
